import { __decorate, __param } from '../../../../../node_modules/tslib/tslib.es6.js';
import { MainContext } from './extHost.protocol.js';
import * as files from 'monaco-editor/esm/vs/platform/files/common/files.js';
import { FileSystemError } from './extHostTypes.js';
import { VSBuffer } from 'monaco-editor/esm/vs/base/common/buffer.js';
import { createDecorator } from 'monaco-editor/esm/vs/platform/instantiation/common/instantiation.js';
import { IExtHostRpcService } from './extHostRpcService.js';
import { IExtHostFileSystemInfo } from './extHostFileSystemInfo.js';
import { toDisposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
let ExtHostConsumerFileSystem = class ExtHostConsumerFileSystem {
    constructor(extHostRpc, fileSystemInfo) {
        this._fileSystemProvider = ( new Map());
        this._proxy = ( extHostRpc.getProxy(MainContext.MainThreadFileSystem));
        const that = this;
        this.value = ( Object.freeze({
            async stat(uri) {
                try {
                    const provider = that._fileSystemProvider.get(uri.scheme);
                    if (!provider) {
                        return await that._proxy.$stat(uri);
                    }
                    await that._proxy.$ensureActivation(uri.scheme);
                    const stat = await provider.stat(uri);
                    return {
                        type: stat.type,
                        ctime: stat.ctime,
                        mtime: stat.mtime,
                        size: stat.size,
                        permissions: stat.permissions
                    };
                }
                catch (err) {
                    ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async readDirectory(uri) {
                try {
                    const provider = that._fileSystemProvider.get(uri.scheme);
                    if (provider) {
                        await that._proxy.$ensureActivation(uri.scheme);
                        return (await provider.readDirectory(uri)).slice();
                    }
                    else {
                        return await that._proxy.$readdir(uri);
                    }
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async createDirectory(uri) {
                try {
                    return await that._proxy.$mkdir(uri);
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async readFile(uri) {
                try {
                    const provider = that._fileSystemProvider.get(uri.scheme);
                    if (provider) {
                        await that._proxy.$ensureActivation(uri.scheme);
                        return (await provider.readFile(uri)).slice();
                    }
                    else {
                        const buff = await that._proxy.$readFile(uri);
                        return buff.buffer;
                    }
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async writeFile(uri, content) {
                try {
                    return await that._proxy.$writeFile(uri, VSBuffer.wrap(content));
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async delete(uri, options) {
                try {
                    const provider = that._fileSystemProvider.get(uri.scheme);
                    if (provider) {
                        await that._proxy.$ensureActivation(uri.scheme);
                        return await provider.delete(uri, { recursive: false, ...options });
                    }
                    else {
                        return await that._proxy.$delete(uri, { recursive: false, useTrash: false, ...options });
                    }
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async rename(oldUri, newUri, options) {
                try {
                    return await that._proxy.$rename(oldUri, newUri, { ...{ overwrite: false }, ...options });
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            async copy(source, destination, options) {
                try {
                    return await that._proxy.$copy(source, destination, { ...{ overwrite: false }, ...options });
                }
                catch (err) {
                    return ExtHostConsumerFileSystem._handleError(err);
                }
            },
            isWritableFileSystem(scheme) {
                const capabilities = fileSystemInfo.getCapabilities(scheme);
                if (typeof capabilities === 'number') {
                    return !((capabilities & 2048) );
                }
                return undefined;
            }
        }));
    }
    static _handleError(err) {
        if (err instanceof FileSystemError) {
            throw err;
        }
        if (!(err instanceof Error)) {
            throw new FileSystemError(String(err));
        }
        if (err.name === 'ENOPRO' || err.message.includes('ENOPRO')) {
            throw FileSystemError.Unavailable(err.message);
        }
        switch (err.name) {
            case files.FileSystemProviderErrorCode.FileExists: throw FileSystemError.FileExists(err.message);
            case files.FileSystemProviderErrorCode.FileNotFound: throw FileSystemError.FileNotFound(err.message);
            case files.FileSystemProviderErrorCode.FileNotADirectory: throw FileSystemError.FileNotADirectory(err.message);
            case files.FileSystemProviderErrorCode.FileIsADirectory: throw FileSystemError.FileIsADirectory(err.message);
            case files.FileSystemProviderErrorCode.NoPermissions: throw FileSystemError.NoPermissions(err.message);
            case files.FileSystemProviderErrorCode.Unavailable: throw FileSystemError.Unavailable(err.message);
            default: throw new FileSystemError(err.message, err.name);
        }
    }
    addFileSystemProvider(scheme, provider) {
        this._fileSystemProvider.set(scheme, provider);
        return toDisposable(() => this._fileSystemProvider.delete(scheme));
    }
};
ExtHostConsumerFileSystem = ( __decorate([
    ( __param(0, IExtHostRpcService)),
    ( __param(1, IExtHostFileSystemInfo))
], ExtHostConsumerFileSystem));
const IExtHostConsumerFileSystem = ( createDecorator('IExtHostConsumerFileSystem'));
export { ExtHostConsumerFileSystem, IExtHostConsumerFileSystem };
